import { PaymentMethodsEnum, type SingleBill } from '@ctct/domain-layer';
import type { SingleBillPayload } from '@ctct/infrastructure-layer';
import type { SingleBillPaymentMethodsPageStore } from '@ctct/state-client-layer';

import type { SecurePayService } from './SecurePayService';
import type { SingleBillPaymentMethodsService } from './SingleBillPaymentMethodsService';
import type { SingleBillStripeElementsService } from './SingleBillStripeElementsService';

export interface SingleBillStripeBillingInfo {
  postalCode: string;
  countryCode: string;
  fullName: string;
  email: string;
  phone: string;
}

export class DataPreparationService {
  constructor(
    private pageStore: SingleBillPaymentMethodsPageStore,
    private singleBill: SingleBill,
    private paymentMethodsService: SingleBillPaymentMethodsService,
    private securePayService: SecurePayService,
    private singleBillStripeElementsService: SingleBillStripeElementsService
  ) {}

  public api() {
    return {
      getPayloadForCheckoutApi: async (
        stripeBillingInfo: SingleBillStripeBillingInfo
      ): Promise<SingleBillPayload['payment_method']> => {
        const selectedPaymentType = this.paymentMethodsService.data().getSelectedMethod();

        const base: SingleBillPayload['payment_method'] = {
          paymentType: {
            name:
              selectedPaymentType === PaymentMethodsEnum.CREDIT_CARD
                ? 'CREDIT CARD'
                : selectedPaymentType.toUpperCase(),
            displayString: selectedPaymentType.toLowerCase(),
          },
          creditCard: null,
          check: null,
          echeck: null,
          payPal: null,
          amazonPay: null,
          origin: null,
        };

        if (this.singleBillStripeElementsService.data().shouldUseStripeElement()) {
          const stripeResponse = await this.singleBillStripeElementsService
            .handlers()
            .submitPaymentMethod(stripeBillingInfo);

          const stripeTokenForPayload = stripeResponse.paymentMethodId;
          const stripeTypeForPayload = stripeResponse.paymentMethodType;
          const stripeCardTypeForPayload = stripeResponse.cardType;
          const stripeCardLast4DigitsForPayload = stripeResponse.cardLast4Digits;
          const stripeCardExpirationDateForPayload = stripeResponse.cardExpirationDate;

          if (!stripeTokenForPayload) {
            throw new Error('Could not create payment method');
          }

          if (!stripeTypeForPayload) {
            throw new Error('Could not determine payment method type');
          }

          this.singleBillStripeElementsService.handlers().reportPersistedStripePaymentMethod(stripeTokenForPayload);

          return {
            ...base,
            paymentType: {
              name: 'STRIPE',
              displayString: 'Stripe',
            },
            stripe: {
              token: stripeTokenForPayload,
              type: stripeTypeForPayload,
              cardType: stripeCardTypeForPayload,
              cardLast4Digits: stripeCardLast4DigitsForPayload,
              cardExpirationDate: stripeCardExpirationDateForPayload,
            },
            creditCard: null,
            echeck: null,
          };
        }

        if (this.paymentMethodsService.data().isCreditCard()) {
          const { securePayEdited } = this.pageStore.getState();
          const shouldValidateCreditCard = securePayEdited || !this.singleBill.getCreditCardInfo();

          const credit_card_info = shouldValidateCreditCard
            ? await this.securePayService.handlers().validateCreditCard()
            : this.singleBill.getCreditCardInfo();

          if (!credit_card_info) {
            throw new Error('Could not get `credit_card_info`');
          }

          return {
            ...base,
            creditCard: {
              cardType: null,
              cardNumber: null,
              cardExpirationDate: null,
              cardSecureCode: null,
              cardHolderName: '',
              validateCardSecureCode: false,
              cardToken: credit_card_info?.cardToken,
              data: null,
              expired: false,
              tokenized: true,
            },
          };
        }

        if (this.paymentMethodsService.data().isEcheck()) {
          const { securePayEdited } = this.pageStore.getState();
          const shouldValidateEcheck = securePayEdited || !this.singleBill.getEcheckInfo();

          const echeck_info = shouldValidateEcheck
            ? await this.securePayService.handlers().validateEcheck()
            : this.singleBill.getEcheckInfo();

          if (!echeck_info) {
            throw new Error();
          }

          return {
            ...base,
            echeck: {
              accountNumber: null,
              accountType: null,
              bankName: null,
              checkNumber: null,
              nameOnBankAccount: null,
              routingNumber: null,
              token: echeck_info.token,
              tokenized: true,
            },
          };
        }

        throw new Error('Unknown payment method');
      },
    };
  }
}
